import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BusinessUnitProvider } from "@/context/BusinessUnitContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import SelectVertical from "@/pages/SelectVertical";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Inventory from "@/pages/Inventory";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Reports from "@/pages/Reports";
import Payments from "@/pages/Payments";
import OrderPrint from "@/pages/OrderPrint";
import OrderCostSheet from "@/pages/OrderCostSheet";
import CustomLabels from "@/pages/CustomLabels";
import CustomerStatement from "@/pages/CustomerStatement";
import AssignStock from "@/pages/AssignStock";
import WorkOrdersBatch from "@/pages/WorkOrdersBatch";
import ShiftStock from "@/pages/ShiftStock";
import Settings from "@/pages/Settings";
import Inquiries from "@/pages/Inquiries";
import CartonLabel from "@/pages/CartonLabel";
import CartonLabelsBulk from "@/pages/CartonLabelsBulk";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BusinessUnitProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/select-vertical"
                element={
                  <ProtectedRoute requireBusinessUnit={false}>
                    <SelectVertical />
                  </ProtectedRoute>
                }
              />
              {/* Carton label page: protected but no Layout (full-page print) */}
              <Route
                path="/label/:box_no"
                element={
                  <ProtectedRoute roles={["admin", "production", "sales", "dispatch"]}>
                    <CartonLabel />
                  </ProtectedRoute>
                }
              />
              {/* Bulk carton labels — many 50×50 labels in one print job */}
              <Route
                path="/labels/bulk"
                element={
                  <ProtectedRoute roles={["admin", "production", "sales", "dispatch"]}>
                    <CartonLabelsBulk />
                  </ProtectedRoute>
                }
              />
              {/* Print page: protected but no Layout (full-page print) */}
              <Route
                path="/orders/:id/print"
                element={
                  <ProtectedRoute>
                    <OrderPrint />
                  </ProtectedRoute>
                }
              />
              {/* Order Cost Sheet (FIFO) — admin/sales only */}
              <Route
                path="/orders/:id/cost-sheet"
                element={
                  <ProtectedRoute roles={["admin", "sales"]}>
                    <OrderCostSheet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/labels/custom"
                element={
                  <ProtectedRoute roles={["admin", "sales", "dispatch", "production"]}>
                    <CustomLabels />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:cid/statement"
                element={
                  <ProtectedRoute roles={["admin", "account", "sales"]}>
                    <CustomerStatement />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/orders" element={<Orders />} />
                <Route
                  path="/work-orders/batch-print"
                  element={
                    <ProtectedRoute roles={["admin", "production", "sales"]}>
                      <WorkOrdersBatch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/:id/assign"
                  element={
                    <ProtectedRoute roles={["admin", "production", "sales"]}>
                      <AssignStock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shift-stock"
                  element={
                    <ProtectedRoute roles={["admin", "production", "dispatch"]}>
                      <ShiftStock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inquiries"
                  element={
                    <ProtectedRoute roles={["admin", "sales"]}>
                      <Inquiries />
                    </ProtectedRoute>
                  }
                />
                <Route path="/inventory" element={<Inventory />} />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute roles={["admin", "sales", "account"]}>
                      <Customers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/suppliers"
                  element={
                    <ProtectedRoute roles={["admin", "production"]}>
                      <Suppliers />
                    </ProtectedRoute>
                  }
                />
                <Route path="/reports" element={<Reports />} />
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute roles={["admin", "account", "sales"]}>
                      <Payments />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </BusinessUnitProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
